import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useAppSelector } from "@redux/hooks";
import { RootState } from "@redux/store/store";
import AccountPanel from "./AccountPanel";
import { useSideMenuBrandLogos } from "../../SideMenu/hooks/useSideMenuBrandLogos";
import { useAdminCenterAccess } from "../../SideMenu/hooks/useAdminCenterAccess";
import { RAIL_WIDTH_EXPANDED } from "../../SideMenu/sideMenuDensity";
import HeaderMenu from "./HeaderMenu";

/**
 * Up to two letters from the user's name, for the avatar.
 *
 * Falls back to the email's first letter, and then to nothing: an avatar with a
 * stray "?" in it reads as an error, while an empty circle reads as a profile
 * that has not loaded yet — which is what it is.
 */
const getInitials = (
  firstName: string | undefined,
  lastName: string | undefined,
  email: string | undefined
): string => {
  const initials = `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.trim();
  if (initials) return initials.toUpperCase();
  return (email?.[0] ?? "").toUpperCase();
};

/**
 * The header's identity control: an avatar that opens `AccountPanel`.
 *
 * Avatar rather than a gear, and separate from the settings control beside it,
 * because the two answer different questions — *who am I* against *what do I
 * configure* (CTX-636). Before this they were the same control in the rail, so
 * the theme toggle and Log out hid behind the same affordance as Users and API.
 *
 * The panel itself is shared with the rail's account card, minus the product
 * list: picking a product is navigation, so that stays in the rail.
 */
export default function HeaderAccountControl() {
  const navigate = useNavigate();
  const { logoSrc, handleImageError } = useSideMenuBrandLogos();

  const { firstName, lastName, email } = useAppSelector(
    (state: RootState) => state.userProfile
  );
  /* Redux rather than the rail's own resolution: the header is a sibling of the
     SideMenu, and re-running `useSideMenuProductSelection` here would fire its
     dispatches a second time on every render of the header. */
  const internalName = useAppSelector(
    (state: RootState) => state.product.selectedProduct
  );

  const { showAdminCenterRow, firstAdminRoute } = useAdminCenterAccess(
    internalName ?? ""
  );

  const initials = useMemo(
    () => getInitials(firstName, lastName, email),
    [firstName, lastName, email]
  );

  /**
   * The rail opens its drill-in panel by setting local state and navigating;
   * from here only the navigation is available — and it is enough, because the
   * rail syncs that panel from the pathname (`isAdminCenterShellPath`). Going
   * through the route keeps one source of truth for "is the Admin center open"
   * instead of lifting the rail's state into a shared store.
   */
  const handleOpenAdminCenter = (e: React.MouseEvent) => {
    e.preventDefault();
    if (firstAdminRoute) navigate(firstAdminRoute);
  };

  return (
    <HeaderMenu
      label="Account"
      panelWidthClass={RAIL_WIDTH_EXPANDED}
      trigger={
        <span
          className="flex h-full w-full items-center justify-center rounded-full bg-semantic-text-brand text-[10px] font-semibold uppercase text-semantic-bg-primary"
          aria-hidden
        >
          {initials}
        </span>
      }
    >
      {(dismiss) => (
        <AccountPanel
          logoSrc={logoSrc}
          onLogoError={handleImageError}
          showAdminCenterRow={showAdminCenterRow}
          onOpenAdminCenter={handleOpenAdminCenter}
          onDismiss={dismiss}
        />
      )}
    </HeaderMenu>
  );
}
